/*
 * Componentes interactivos de las secciones ws-* (sin dependencias):
 * aparición al hacer scroll, contadores animados, pestañas de funcionalidades con avance automático,
 * carruseles con flechas y puntos, muro de opiniones ("ver más" + visor de fotos).
 * Respeta prefers-reduced-motion.
 */
(function () {
  'use strict';

  var reducirMovimiento = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hayObservador = 'IntersectionObserver' in window;

  /* ---------- Aparición al hacer scroll ---------- */
  function iniciarApariciones() {
    var elementos = document.querySelectorAll('[data-reveal]');
    if (reducirMovimiento || !hayObservador) {
      elementos.forEach(function (el) { el.classList.add('ws-visible'); });
      return;
    }
    document.documentElement.classList.add('ws-animar');
    var observador = new IntersectionObserver(function (entradas) {
      entradas.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('ws-visible'); observador.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    elementos.forEach(function (el) { observador.observe(el); });
  }

  /* ---------- Contadores (solo si la cifra es numérica) ---------- */
  function iniciarContadores() {
    var elementos = document.querySelectorAll('[data-ws-contar]');
    if (reducirMovimiento || !hayObservador) return;
    var observador = new IntersectionObserver(function (entradas) {
      entradas.forEach(function (e) {
        if (!e.isIntersecting) return;
        observador.unobserve(e.target);
        var objetivo = parseInt(String(e.target.dataset.wsContar).replace(/\D/g, ''), 10);
        if (!objetivo || !/^\d[\d.]*$/.test(e.target.dataset.wsContar)) return;
        var inicio = null;
        var duracion = 1200;
        function paso(t) {
          if (!inicio) inicio = t;
          var avance = Math.min(1, (t - inicio) / duracion);
          var suavizado = 1 - Math.pow(1 - avance, 3);
          e.target.textContent = Math.round(objetivo * suavizado).toLocaleString('es-ES');
          if (avance < 1) requestAnimationFrame(paso);
        }
        requestAnimationFrame(paso);
      });
    }, { threshold: 0.6 });
    elementos.forEach(function (el) { observador.observe(el); });
  }

  /* ---------- Pestañas de funcionalidades ---------- */
  function iniciarPestanas() {
    document.querySelectorAll('[data-ws-pestanas]').forEach(function (caja) {
      var tabs = Array.prototype.slice.call(caja.querySelectorAll('[role="tab"]'));
      var paneles = Array.prototype.slice.call(caja.querySelectorAll('[role="tabpanel"]'));
      var intervalo = parseInt(caja.dataset.intervalo, 10) || 5000;
      var actual = 0;
      var temporizador = null;

      function activar(i, enfocar) {
        actual = (i + tabs.length) % tabs.length;
        tabs.forEach(function (t, j) {
          var activo = j === actual;
          t.setAttribute('aria-selected', activo ? 'true' : 'false');
          t.tabIndex = activo ? 0 : -1;
          t.classList.remove('ws-en-curso');
        });
        paneles.forEach(function (p, j) { p.hidden = j !== actual; });
        if (enfocar) tabs[actual].focus();
        if (temporizador !== null) {
          void tabs[actual].offsetWidth; // reinicia la animación de la barra
          tabs[actual].style.setProperty('--ws-duracion', intervalo + 'ms');
          tabs[actual].classList.add('ws-en-curso');
        }
      }

      function parar() { clearInterval(temporizador); temporizador = null; tabs.forEach(function (t) { t.classList.remove('ws-en-curso'); }); }

      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { parar(); activar(i); });
        t.addEventListener('keydown', function (ev) {
          if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') { ev.preventDefault(); parar(); activar(actual + 1, true); }
          if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') { ev.preventDefault(); parar(); activar(actual - 1, true); }
        });
      });

      if (!reducirMovimiento && tabs.length > 1) {
        temporizador = setInterval(function () { activar(actual + 1); }, intervalo);
        activar(0);
        caja.addEventListener('mouseenter', parar, { once: true });
      }
    });
  }

  /* ---------- Carruseles con flechas y puntos ---------- */
  function iniciarCarruseles() {
    document.querySelectorAll('[data-ws-carrusel]').forEach(function (envoltorio) {
      var pista = envoltorio.querySelector('[data-ws-pista]');
      if (!pista) return;
      var puntos = Array.prototype.slice.call(envoltorio.querySelectorAll('.ws-puntos span'));
      var diapositivas = Array.prototype.slice.call(pista.children);

      function indiceVisible() {
        var centro = pista.scrollLeft + pista.clientWidth / 2;
        var mejor = 0;
        diapositivas.forEach(function (d, i) {
          if (Math.abs(d.offsetLeft + d.offsetWidth / 2 - centro) < Math.abs(diapositivas[mejor].offsetLeft + diapositivas[mejor].offsetWidth / 2 - centro)) mejor = i;
        });
        return mejor;
      }
      function irA(i) {
        var d = diapositivas[Math.max(0, Math.min(diapositivas.length - 1, i))];
        if (d) pista.scrollTo({ left: d.offsetLeft - (pista.clientWidth - d.offsetWidth) / 2, behavior: reducirMovimiento ? 'auto' : 'smooth' });
      }
      function pintarPuntos() {
        var i = indiceVisible();
        puntos.forEach(function (p, j) { p.classList.toggle('activo', j === i); });
      }
      var anterior = envoltorio.querySelector('[data-ws-anterior]');
      var siguiente = envoltorio.querySelector('[data-ws-siguiente]');
      if (anterior) anterior.addEventListener('click', function () { irA(indiceVisible() - 1); });
      if (siguiente) siguiente.addEventListener('click', function () { irA(indiceVisible() + 1); });
      puntos.forEach(function (p, j) { p.addEventListener('click', function () { irA(j); }); });
      pista.addEventListener('scroll', function () { window.requestAnimationFrame(pintarPuntos); }, { passive: true });
      pintarPuntos();
    });
  }

  /* ---------- Opiniones: ver más + visor de fotos ---------- */
  function iniciarOpiniones() {
    document.querySelectorAll('[data-ws-ver-mas]').forEach(function (boton) {
      var seccion = boton.closest('.ws-resenas');
      boton.addEventListener('click', function () {
        var ocultas = seccion.querySelectorAll('[data-ws-extra][hidden]');
        Array.prototype.slice.call(ocultas, 0, 6).forEach(function (el) { el.hidden = false; el.classList.add('ws-visible', 'ws-mostrada'); });
        if (seccion.querySelectorAll('[data-ws-extra][hidden]').length === 0) boton.remove();
      });
    });
    document.querySelectorAll('[data-ws-visor]').forEach(function (visor) {
      var seccion = visor.closest('.ws-resenas');
      var imagen = visor.querySelector('img');
      seccion.addEventListener('click', function (ev) {
        var disparador = ev.target.closest('[data-ws-ampliar]');
        if (!disparador || typeof visor.showModal !== 'function') return;
        imagen.src = disparador.dataset.wsAmpliar;
        visor.showModal();
      });
      visor.addEventListener('click', function (ev) {
        if (ev.target === visor || ev.target.closest('[data-ws-cerrar-visor]')) visor.close();
      });
    });
  }

  /* ---------- Opiniones: filtros por tema ---------- */
  function iniciarFiltrosOpiniones() {
    document.querySelectorAll('.ws-resenas__chips').forEach(function (grupo) {
      var seccion = grupo.closest('.ws-resenas');
      var verMas = seccion.querySelector('[data-ws-ver-mas]');
      grupo.addEventListener('click', function (ev) {
        var chip = ev.target.closest('[data-ws-filtro]');
        if (!chip) return;
        var filtro = chip.dataset.wsFiltro;
        grupo.querySelectorAll('[data-ws-filtro]').forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        seccion.querySelectorAll('.ws-resena').forEach(function (r) {
          var etiquetas = ' ' + (r.dataset.wsEtiquetas || '') + ' ';
          var coincide = !filtro || etiquetas.indexOf(' ' + filtro + ' ') !== -1;
          if (filtro) { r.hidden = !coincide; } else { r.hidden = r.hasAttribute('data-ws-extra') && !r.classList.contains('ws-mostrada'); }
          if (coincide) r.classList.add('ws-visible');
        });
        if (verMas) verMas.hidden = Boolean(filtro);
      });
    });
  }

  /* ---------- Puntos interactivos sobre la foto ---------- */
  function iniciarPuntos() {
    document.querySelectorAll('[data-ws-hotspots]').forEach(function (seccion) {
      var puntos = seccion.querySelectorAll('[data-ws-punto]');
      var detalles = seccion.querySelectorAll('[data-ws-detalle]');
      function marcar(i) {
        puntos.forEach(function (p) { p.classList.toggle('activo', p.dataset.wsPunto === String(i)); });
      }
      puntos.forEach(function (p) {
        p.addEventListener('click', function () {
          detalles.forEach(function (d) { d.open = d.dataset.wsDetalle === p.dataset.wsPunto; });
          marcar(p.dataset.wsPunto);
        });
      });
      detalles.forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          detalles.forEach(function (o) { if (o !== d) o.open = false; });
          marcar(d.dataset.wsDetalle);
        });
      });
      marcar(0);
    });
  }

  /* ---------- Manómetro animado ---------- */
  function iniciarManometros() {
    document.querySelectorAll('[data-ws-manometro]').forEach(function (dial) {
      var objetivo = parseFloat(dial.dataset.objetivo) || 2.5;
      var maximo = parseFloat(dial.dataset.maximo) || 4;
      var fraccion = Math.min(1, objetivo / maximo);
      var lectura = dial.querySelector('[data-ws-presion]');
      // Mismos decimales que el objetivo configurado: "3.0" → "3,0", "15" → "15".
      var decimales = (String(dial.dataset.formato || dial.dataset.objetivo || '').split('.')[1] || '').length;
      function pintar(f) {
        dial.style.setProperty('--ws-fraccion', f);
        lectura.textContent = (maximo * f).toFixed(decimales).replace('.', ',');
      }
      if (reducirMovimiento || !hayObservador) { pintar(fraccion); dial.classList.add('ws-parado'); return; }
      pintar(0);
      var observador = new IntersectionObserver(function (entradas) {
        if (!entradas[0].isIntersecting) return;
        observador.disconnect();
        var inicio = null;
        var duracion = 2600;
        function paso(t) {
          if (!inicio) inicio = t;
          var avance = Math.min(1, (t - inicio) / duracion);
          pintar(fraccion * (1 - Math.pow(1 - avance, 2)));
          if (avance < 1) requestAnimationFrame(paso);
          else dial.classList.add('ws-parado');
        }
        requestAnimationFrame(paso);
      }, { threshold: 0.5 });
      observador.observe(dial);
    });
  }

  /* ---------- Cuenta atrás hacia una fecha de fin real ---------- */
  function iniciarCuentasAtras() {
    document.querySelectorAll('[data-ws-cuenta]').forEach(function (barra) {
      var fin = Date.parse(barra.dataset.fin);
      if (isNaN(fin)) return;
      var campos = {
        dias: barra.querySelector('[data-ws-dias]'), horas: barra.querySelector('[data-ws-horas]'),
        minutos: barra.querySelector('[data-ws-minutos]'), segundos: barra.querySelector('[data-ws-segundos]'),
      };
      var dos = function (n) { return (n < 10 ? '0' : '') + n; };
      function pintar() {
        var resta = Math.floor((fin - Date.now()) / 1000);
        if (resta <= 0) { barra.hidden = true; clearInterval(intervalo); return; } // la oferta ha terminado de verdad
        barra.hidden = false;
        campos.dias.textContent = Math.floor(resta / 86400);
        campos.horas.textContent = dos(Math.floor(resta % 86400 / 3600));
        campos.minutos.textContent = dos(Math.floor(resta % 3600 / 60));
        campos.segundos.textContent = dos(resta % 60);
      }
      var intervalo = setInterval(pintar, 1000);
      pintar();
    });
  }

  /* ---------- Precios de los packs según la variante elegida ---------- */
  // Respeta el formato de la tienda: si el precio original empieza por "€" (formato de Shopify), lo mantiene.
  function formatoComo(ejemplo, centimos) {
    var cifra = (centimos / 100).toFixed(2).replace('.', ',');
    return /^\s*€/.test(ejemplo || '') ? '€' + cifra : cifra + ' €';
  }
  window.wsPreciosPack = function (centimos) {
    var oferta = document.querySelector('[data-ws-descuento]');
    if (!oferta) return;
    var descuento = parseFloat(oferta.dataset.wsDescuento) || 0;
    var ahorro = Math.round(centimos * descuento);
    var valores = { wsPackUno: centimos, wsPackTachado: centimos * 2, wsPackDos: centimos * 2 - ahorro, wsPackAhorro: ahorro };
    Object.keys(valores).forEach(function (clave) {
      var atributo = 'data-' + clave.replace(/[A-Z]/g, function (m) { return '-' + m.toLowerCase(); });
      oferta.querySelectorAll('[' + atributo + ']').forEach(function (el) { el.textContent = formatoComo(el.textContent, valores[clave]); });
    });
  };

  function iniciar() {
    iniciarCuentasAtras();
    iniciarFiltrosOpiniones();
    iniciarPuntos();
    iniciarManometros();
    iniciarApariciones();
    iniciarContadores();
    iniciarPestanas();
    iniciarCarruseles();
    iniciarOpiniones();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();
})();

// Vídeo principal de la ficha: se reproduce solo, en silencio y en bucle (como en la web local),
// en lugar de esperar a que el cliente pulse el botón de play de Dawn.
(function () {
  function reproducirVideoPrincipal() {
    const diferido = document.querySelector('.product__media-list deferred-media, .product-media-container deferred-media');
    if (!diferido || typeof diferido.loadContent !== 'function') return;
    diferido.loadContent(false);
    const video = diferido.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.play().catch(() => {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reproducirVideoPrincipal);
  else reproducirVideoPrincipal();
})();
